import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { LineBasedInvoiceForm } from '@/components/features/invoicing/LineBasedInvoiceForm'
import { formatCurrency } from '@/lib/format'
import type { CreateInvoiceLineDto, ProjectInvoice } from '@/types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayIso(): string {
  return new Date().toISOString().split('T')[0]
}

function computeTotalTtc(lines: CreateInvoiceLineDto[]): number {
  return lines
    .filter(l => l.vatPercent != null && l.unitPriceWithoutTax != null)
    .reduce((sum, l) => {
      const qty = l.quantity ?? 1
      const ht = Math.round(qty * (l.unitPriceWithoutTax ?? 0) * (1 - (l.discountPercent ?? 0)) * 100) / 100
      const tax = Math.round(ht * (l.vatPercent!) / 100 * 100) / 100
      return sum + ht + tax
    }, 0)
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LineBasedInvoicePageContentProps {
  projectId: string
  title: string
  description: string
  remainingAmount: number
  initialLines: CreateInvoiceLineDto[]
  /** UI context only — 'direct_sale' is mapped to 'standalone' when calling the API */
  invoiceType: 'standalone' | 'direct_sale' | 'credit_note'
  error: string | null
  /** Available invoices for credit note reference dropdown */
  projectInvoices?: ProjectInvoice[]
  /** Total already invoiced — used as credit note ceiling */
  totalInvoiced?: number
  /**
   * Default VAT rate for new lines, forwarded to LineBasedInvoiceForm.
   * Consumers should pass the project's `defaultVat` (or the referenced
   * invoice's rate when the user picks one for a credit note). Fallback
   * inside the form is 21 % if the value is missing or unsupported.
   */
  defaultVatPercent?: number
}

// ── Component ─────────────────────────────────────────────────────────────────

// Session storage key scoped by project + invoice type
function storageKey(projectId: string, invoiceType: string): string {
  return `invoice-draft:${projectId}:${invoiceType}`
}

interface DraftState {
  invoiceDate: string
  lines: CreateInvoiceLineDto[]
  referencedInvoiceIds: string[]
  // SOLY-CN-003 — optional credit note reason (Mode C), persisted with the draft.
  reason?: string
}

function loadDraft(projectId: string, invoiceType: string): DraftState | null {
  try {
    const raw = sessionStorage.getItem(storageKey(projectId, invoiceType))
    if (!raw) return null
    return JSON.parse(raw) as DraftState
  } catch {
    return null
  }
}

function saveDraft(projectId: string, invoiceType: string, state: DraftState): void {
  try {
    sessionStorage.setItem(storageKey(projectId, invoiceType), JSON.stringify(state))
  } catch {
    // quota exceeded — ignore
  }
}

export function LineBasedInvoicePageContent({
  projectId,
  title,
  description,
  remainingAmount,
  initialLines,
  invoiceType,
  error,
  projectInvoices,
  totalInvoiced,
  defaultVatPercent,
}: LineBasedInvoicePageContentProps) {
  const navigate = useNavigate()
  const location = useLocation()

  // Restore from location.state (back from preview) or sessionStorage, or defaults
  const locationState = location.state as { invoiceDate?: string; lines?: CreateInvoiceLineDto[]; referencedInvoiceIds?: string[]; reason?: string } | null
  const savedDraft = loadDraft(projectId, invoiceType)

  const [invoiceDate, setInvoiceDate] = useState(
    locationState?.invoiceDate ?? savedDraft?.invoiceDate ?? todayIso()
  )
  const [lines, setLines] = useState<CreateInvoiceLineDto[]>(
    locationState?.lines ?? savedDraft?.lines ?? initialLines
  )
  const [referencedInvoiceIds, setReferencedInvoiceIds] = useState<string[]>(
    locationState?.referencedInvoiceIds ?? savedDraft?.referencedInvoiceIds ?? []
  )
  // SOLY-CN-003 — optional reason (credit note only).
  const [reason, setReason] = useState<string>(
    locationState?.reason ?? savedDraft?.reason ?? ''
  )

  // Persist draft to sessionStorage whenever state changes
  useEffect(() => {
    saveDraft(projectId, invoiceType, { invoiceDate, lines, referencedInvoiceIds, reason })
  }, [projectId, invoiceType, invoiceDate, lines, referencedInvoiceIds, reason])

  const isCreditNote = invoiceType === 'credit_note'
  const hasCommercialLine = lines.some(l => l.vatPercent != null && l.unitPriceWithoutTax != null)
  const totalTtc = computeTotalTtc(lines)
  // Credit notes are not capped by remaining amount — backend validates against already invoiced
  const exceedsRemaining = !isCreditNote && totalTtc > remainingAmount + 0.01
  const canPreview = hasCommercialLine && !exceedsRemaining && !!invoiceDate

  const handlePreview = () => {
    if (!canPreview) return
    navigate(`/projets/${projectId}/invoices/preview`, {
      state: {
        invoiceType,
        invoiceDate,
        lines,
        ...(isCreditNote && referencedInvoiceIds.length > 0 ? { referencedInvoiceIds } : {}),
        ...(isCreditNote && reason.trim() ? { reason: reason.trim() } : {}),
      },
    })
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => navigate(`/projects/${projectId}?tab=invoices`)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold">{title}</h1>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-muted-foreground">
          {error}
        </div>
      ) : (
        <>
          {/* Summary card */}
          <Card>
            <CardContent className="py-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {isCreditNote ? 'Déjà facturé' : 'Restant à facturer'}
              </span>
              <span className="text-lg font-semibold tabular-nums">
                {formatCurrency(isCreditNote ? (totalInvoiced ?? 0) : remainingAmount)}
              </span>
            </CardContent>
          </Card>

          {/* Invoice date */}
          <div className="space-y-1.5">
            <Label htmlFor="invoiceDate">{isCreditNote ? 'Date de la note de crédit' : 'Date de la facture'}</Label>
            <Input
              id="invoiceDate"
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="w-48"
            />
          </div>

          {/* Referenced invoices (credit note only) */}
          {isCreditNote && projectInvoices && projectInvoices.length > 0 && (
            <div className="space-y-1.5">
              <Label>Factures référencées (optionnel)</Label>
              <div className="space-y-1">
                {projectInvoices.map((inv) => (
                  <label key={inv.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={referencedInvoiceIds.includes(inv.id)}
                      onChange={(e) => {
                        setReferencedInvoiceIds((prev) =>
                          e.target.checked
                            ? [...prev, inv.id]
                            : prev.filter((id) => id !== inv.id)
                        )
                      }}
                      className="rounded border-gray-300"
                    />
                    <span>{inv.number}</span>
                    <span className="text-muted-foreground">— {formatCurrency(inv.amountWithTax)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Reason (credit note only) — SOLY-CN-003 */}
          {isCreditNote && (
            <div className="space-y-1.5">
              <Label htmlFor="credit-note-reason">Motif (optionnel)</Label>
              <Textarea
                id="credit-note-reason"
                data-testid="credit-note-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex. : Changement dénomination société"
                maxLength={500}
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Affiché sur la note de crédit, pour la clarté en cas de contrôle.
              </p>
            </div>
          )}

          {/* Line editor */}
          <LineBasedInvoiceForm
            projectId={projectId}
            lines={lines}
            onChange={setLines}
            remainingAmount={remainingAmount}
            isCreditNote={isCreditNote}
            totalInvoiced={totalInvoiced}
            defaultVatPercent={defaultVatPercent}
          />

          {/* Preview button */}
          <div className="flex justify-end pt-2">
            <Button onClick={handlePreview} disabled={!canPreview}>
              Aperçu PDF
            </Button>
          </div>

          {!hasCommercialLine && lines.length > 0 && (
            <p className="text-xs text-muted-foreground text-right">
              Au moins une ligne commerciale (avec prix et TVA) est requise.
            </p>
          )}
        </>
      )}
    </div>
  )
}
